import React, { useEffect, useMemo, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import * as QRCode from 'qrcode';
import { QRCodeToDataURLOptions } from 'qrcode';
import {
    ChallengePageEntrySupportDto,
    CommunityPageEntrySupportDto,
    TeamSupportDto
} from '@api/ChallengeClient';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import { Switch } from '@components/ui/switch';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { cn } from '@core/utils/cn';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';
import {
    GO_MOVEREPUBLIC_DEV_URL,
    GO_MOVEREPUBLIC_PROD_URL
} from '@src/app/config';
import { TenantPageEntrySupportDto } from '@src/core/api/TenantClient';
import { GeneratedUrlPreview } from '@src/onboarding/smart-connect/generated-url-preview';
import { QrCodeConfig } from '@src/onboarding/smart-connect/qr-code-config';
import { SelectionPreview } from '@src/onboarding/smart-connect/selection-preview';
import { SelectionSection } from '@src/onboarding/smart-connect/selection-section';
import {
    REQUIRED_PROFILE_FIELDS,
    RequiredProfileFields
} from '@src/onboarding/smart-connect/required-profile-fields';
import { SavedOnboardings } from '@src/onboarding/smart-connect/saved-onboardings';
import {
    buildSmartConnectParams,
    inlineJoinUrl,
    storedJoinUrl,
    withWhitelabel
} from '@src/onboarding/smart-connect/smart-connect-params';
import { buildSmartConnectValidationContext } from '@src/onboarding/smart-connect/smart-connect-validation';

type QRConfig = {
    size: number;
    level: 'L' | 'M' | 'Q' | 'H';
    marginSize: number;
};

type MoveRepublicGlobalCommunityMembershipMode =
    | 'auto-join'
    | 'invitation'
    | 'disabled';

export const SmartConnectPage = () => {
    const baseUrl = getMobileBaseUrl();
    const [selectedTenants, setSelectedTenants] = useState<
        TenantPageEntrySupportDto[]
    >([]);
    const [selectedCommunities, setSelectedCommunities] = useState<
        CommunityPageEntrySupportDto[]
    >([]);
    const [selectedTeams, setSelectedTeams] = useState<TeamSupportDto[]>([]);
    const [selectedChallenges, setSelectedChallenges] = useState<
        ChallengePageEntrySupportDto[]
    >([]);
    const [communityTeamRequired, setCommunityTeamRequired] = useState<
        string[]
    >([]);
    const [
        moveRepublicGlobalCommunityMembershipMode,
        setMoveRepublicGlobalCommunityMembershipMode
    ] = useState<MoveRepublicGlobalCommunityMembershipMode>('disabled');
    const [tourGuideEnabled, setTourGuideEnabled] = useState(false);
    const [requiredProfileFields, setRequiredProfileFields] = useState<
        string[]
    >([]);
    const [whitelabelCommunityId, setWhitelabelCommunityId] = useState<
        number | null
    >(null);
    const [onboardingName, setOnboardingName] = useState('');
    const [savedKey, setSavedKey] = useState<string | null>(null);
    const [savedCount, setSavedCount] = useState(0);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<QRConfig>({
        size: 500,
        level: 'L',
        marginSize: 4
    });

    const validationContext = useMemo(
        () =>
            buildSmartConnectValidationContext({
                selectedTenants,
                selectedCommunities,
                selectedTeams,
                selectedChallenges,
                communityTeamRequired
            }),
        [
            selectedTenants,
            selectedCommunities,
            selectedTeams,
            selectedChallenges,
            communityTeamRequired
        ]
    );

    useEffect(() => {
        setCommunityTeamRequired((previous) => {
            if (
                previous.length ===
                    validationContext.communityTeamRequired.length &&
                previous.every(
                    (code, index) =>
                        code === validationContext.communityTeamRequired[index]
                )
            ) {
                return previous;
            }

            return validationContext.communityTeamRequired;
        });
    }, [validationContext.communityTeamRequired]);

    const downloadQRCode = async () => {
        if (validationContext.hasBlockingIssues) {
            return;
        }

        try {
            const options: QRCodeToDataURLOptions = {
                width: config.size,
                margin: config.marginSize,
                errorCorrectionLevel: config.level,
                type: 'image/png'
            };

            const dataUrl = await QRCode.toDataURL(generatedUrl, options);

            const link = document.createElement('a');
            link.download = 'qrcode.png';
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error generating QR code:', err);
        }
    };

    const whitelabelCommunity = selectedCommunities.find(
        (community) =>
            community.id === whitelabelCommunityId &&
            community.whitelabelId &&
            community.whitelabelEnabled !== false
    );

    const params = useMemo(
        () =>
            buildSmartConnectParams({
                tenantCodes: invitationCodes(selectedTenants),
                communityCodes: invitationCodes(
                    whitelabelCommunityFirst(
                        selectedCommunities,
                        whitelabelCommunityId
                    )
                ),
                communityTeamRequiredCodes:
                    validationContext.communityTeamRequired,
                teamCodes: invitationCodes(selectedTeams),
                challengeCodes: invitationCodes(selectedChallenges),
                tourGuideEnabled,
                requiredProfileFieldCodes: REQUIRED_PROFILE_FIELDS.filter(
                    (field) => requiredProfileFields.includes(field.code)
                ).map((field) => field.code),
                moveRepublicGlobalCommunityMembershipMode
            }),
        [
            selectedTenants,
            selectedCommunities,
            whitelabelCommunityId,
            selectedTeams,
            selectedChallenges,
            moveRepublicGlobalCommunityMembershipMode,
            tourGuideEnabled,
            requiredProfileFields,
            validationContext.communityTeamRequired
        ]
    );

    useEffect(() => {
        setSavedKey(null);
    }, [params, whitelabelCommunity]);

    const generatedUrl = savedKey
        ? storedJoinUrl(baseUrl, savedKey)
        : inlineJoinUrl(
              baseUrl,
              withWhitelabel(params, whitelabelCommunity?.whitelabelId)
          );

    const saveOnboarding = async () => {
        setSaving(true);
        try {
            const { data } = await injectTSDI(
                Api
            ).mobileClient.supportApi.smartConnectOnboardingSupportControllerCreate(
                {
                    name: onboardingName.trim() || undefined,
                    params: withWhitelabel(
                        params,
                        whitelabelCommunity?.whitelabelId
                    )
                }
            );
            setSavedKey(data.key ?? null);
            setSavedCount((count) => count + 1);
            toast.success(`Onboarding saved as ${data.key}`);
        } catch (err) {
            toast.error('Saving the onboarding failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mx-auto w-full max-w-[1400px]">
            <PageHeader
                title="SmartConnect"
                description="Generate a join link and QR code for the selected entries"
            />

            <div className="grid gap-6 lg:grid-cols-[minmax(340px,1fr)_1.4fr]">
                <div className="flex flex-col gap-4">
                    <SelectionSection
                        selectedTenants={selectedTenants}
                        setSelectedTenants={setSelectedTenants}
                        selectedTeams={selectedTeams}
                        setSelectedTeams={setSelectedTeams}
                        selectedCommunities={selectedCommunities}
                        setSelectedCommunities={setSelectedCommunities}
                        selectedChallenges={selectedChallenges}
                        setSelectedChallenges={setSelectedChallenges}
                    />
                    <QrCodeConfig config={config} onConfigChange={setConfig} />
                    <Card>
                        <CardContent>
                            <Select
                                label="Move Republic Global Community"
                                value={
                                    moveRepublicGlobalCommunityMembershipMode
                                }
                                options={[
                                    { value: 'auto-join', label: 'Auto-join' },
                                    {
                                        value: 'invitation',
                                        label: 'Invitation'
                                    },
                                    { value: 'disabled', label: 'Disabled' }
                                ]}
                                onChange={
                                    setMoveRepublicGlobalCommunityMembershipMode
                                }
                            />
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent>
                            <label className="flex cursor-pointer items-center gap-2 text-sm">
                                <Switch
                                    checked={tourGuideEnabled}
                                    onCheckedChange={setTourGuideEnabled}
                                />
                                Enable Tour Guide
                            </label>
                        </CardContent>
                    </Card>
                    <RequiredProfileFields
                        selected={requiredProfileFields}
                        onChange={setRequiredProfileFields}
                    />
                    <Card>
                        <CardContent className="flex flex-col gap-4">
                            <TextField
                                type="text"
                                label="Onboarding name"
                                value={onboardingName}
                                onChange={(value) =>
                                    setOnboardingName(String(value))
                                }
                            />
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={saveOnboarding}
                                disabled={
                                    saving ||
                                    savedKey !== null ||
                                    validationContext.hasBlockingIssues
                                }
                            >
                                {savedKey
                                    ? `Saved as ${savedKey}`
                                    : 'Save onboarding'}
                            </Button>
                        </CardContent>
                    </Card>
                    <Button
                        size="lg"
                        className="w-full"
                        onClick={downloadQRCode}
                        disabled={validationContext.hasBlockingIssues}
                    >
                        Download QR Code
                    </Button>
                </div>

                <Card>
                    <CardContent>
                        <h2 className="mb-4 text-lg font-semibold tracking-tight">
                            Preview
                        </h2>
                        <SelectionPreview
                            selectedTenants={selectedTenants}
                            selectedCommunities={selectedCommunities}
                            selectedTeams={selectedTeams}
                            selectedChallenges={selectedChallenges}
                            communityTeamRequired={
                                validationContext.communityTeamRequired
                            }
                            setSelectedTenants={setSelectedTenants}
                            setSelectedCommunities={setSelectedCommunities}
                            setSelectedTeams={setSelectedTeams}
                            setSelectedChallenges={setSelectedChallenges}
                            setCommunityTeamRequired={setCommunityTeamRequired}
                            whitelabelCommunityId={whitelabelCommunityId}
                            setWhitelabelCommunityId={setWhitelabelCommunityId}
                        />

                        <div
                            className={cn(
                                'my-6 flex w-full justify-center [&_canvas]:block [&_canvas]:h-auto [&_canvas]:max-w-full',
                                validationContext.hasBlockingIssues &&
                                    '[&_canvas]:opacity-50'
                            )}
                        >
                            <QRCodeCanvas
                                value={generatedUrl}
                                size={Math.min(config.size, 320)}
                                level={config.level}
                                marginSize={config.marginSize}
                            />
                        </div>
                        <GeneratedUrlPreview generatedUrl={generatedUrl} />
                    </CardContent>
                </Card>
            </div>
            <SavedOnboardings baseUrl={baseUrl} reloadToken={savedCount} />
        </div>
    );
};

function whitelabelCommunityFirst(
    communities: CommunityPageEntrySupportDto[],
    whitelabelCommunityId: number | null
): CommunityPageEntrySupportDto[] {
    return [
        ...communities.filter(
            (community) => community.id === whitelabelCommunityId
        ),
        ...communities.filter(
            (community) => community.id !== whitelabelCommunityId
        )
    ];
}

function invitationCodes(entries: { invitationCode?: string }[]): string[] {
    return entries
        .map((entry) => entry.invitationCode)
        .filter((code): code is string => !!code);
}

export function getMobileBaseUrl() {
    const { env } = injectTSDI(Api);
    return env === 'dev' ? GO_MOVEREPUBLIC_DEV_URL : GO_MOVEREPUBLIC_PROD_URL;
}
